import React, { useState } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import translate from "../../../i18n/translate";
import ApiRequest from "../../../actions/ApiRequest";

// Turns the login code off for one seller, and back on.
//
// Signing in from a machine we have not seen before sends a code over
// WhatsApp, and without it the seller cannot get in. For a few accounts that
// code can never arrive - the number on file is dead, the handset is not
// theirs any more, the provider is down - and reading the code back out (the
// Login Code tab) only rescues one login at a time. This skips the step for
// that account until an admin puts it back.
//
// It leaves the password as the only thing in front of the account, so the
// backend allows admins only and writes every read and every change to the
// security log.
const SellerOtpToggle = ({ isDarktheme }) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seller, setSeller] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const lookup = async (e) => {
    e.preventDefault();

    const wanted = query.trim();
    if (!wanted) return;

    setLoading(true);
    setSeller(null);
    setError(null);
    setNotice(null);

    try {
      const res = await ApiRequest.post(
        `seller_otp?operation=get&sellerid=${encodeURIComponent(wanted)}`
      );

      if (res?.data?.status === "success") {
        setSeller(res.data);
      } else {
        setError(res?.data?.reason || "Something Went Wrong!");
      }
    } catch (err) {
      setError("Something Went Wrong!");
    } finally {
      setLoading(false);
    }
  };

  const setDisabled = async (disabled) => {
    if (!seller) return;

    setSaving(true);
    setError(null);
    setNotice(null);

    try {
      const res = await ApiRequest.post(
        `seller_otp?operation=update&sellerid=${encodeURIComponent(
          seller.sellerid
        )}&disabled=${disabled ? "true" : "false"}`
      );

      if (res?.data?.status === "success") {
        setSeller(res.data);
        setNotice(res.data.reason || null);
      } else {
        setError(res?.data?.reason || "Something Went Wrong!");
      }
    } catch (err) {
      setError("Something Went Wrong!");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ padding: 3, color: isDarktheme ? "#fff" : "inherit" }}>
      <Typography variant="h5" sx={{ marginBottom: 1 }}>
        {translate("Skip Login Code")}
      </Typography>

      <Typography variant="body2" sx={{ marginBottom: 3, opacity: 0.75, maxWidth: "62ch" }}>
        {translate("skipLoginCodeHint")}
      </Typography>

      <Box
        component="form"
        onSubmit={lookup}
        sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", marginBottom: 3 }}
      >
        <TextField
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          label={translate("Seller ID")}
          sx={{ minWidth: 260 }}
        />
        <Button type="submit" variant="contained" disabled={loading || !query.trim()}>
          {loading ? <CircularProgress size={20} color="inherit" /> : translate("Search")}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ maxWidth: 620 }}>{error}</Alert>}

      {seller && (
        <Paper sx={{ padding: 3, maxWidth: 620 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {seller.name}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {seller.sellerid} &middot; {seller.number}
          </Typography>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center", marginTop: 2 }}>
            <Chip
              size="small"
              color={seller.otp_disabled ? "warning" : "success"}
              label={
                seller.otp_disabled
                  ? translate("No code required")
                  : translate("Code required")
              }
            />
          </Box>

          {seller.otp_disabled && (
            <Alert severity="warning" sx={{ marginTop: 2 }}>
              {translate("skipLoginCodeWarning")}
            </Alert>
          )}

          {notice && (
            <Alert severity="info" sx={{ marginTop: 2 }}>
              {notice}
            </Alert>
          )}

          <Box sx={{ marginTop: 3 }}>
            {seller.otp_disabled ? (
              <Button variant="contained" disabled={saving} onClick={() => setDisabled(false)}>
                {saving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  translate("Turn the code back on")
                )}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="warning"
                disabled={saving}
                onClick={() => setDisabled(true)}
              >
                {saving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  translate("Turn the code off")
                )}
              </Button>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

const mapStateToProps = ({ darkTheme: { isDarktheme } }) => ({ isDarktheme });

export default connect(mapStateToProps)(SellerOtpToggle);
